export interface Product {
    id: string;
    name: string;
    category: string;
    glbUrl: string;
    thumbnail: string;
    description: string;
    defaultParts: string[]; // e.g., ['Body', 'Sleeves', 'Collar']
    materialConfig?: {
        useGlassMaterial?: boolean;
        targetScale?: number;
        forceCylindricalUVs?: boolean;
        invertX?: boolean; // Invert X-axis controls
        invertY?: boolean; // Invert Y-axis controls 
        textureFlip?: boolean; // Explicitly set texture flipY (default: true)
        textureFlipX?: boolean; // Explicitly mirror texture horizontally
        initialRotationY?: number; // Initial Y rotation in radians
        initialRotationX?: number; // Initial X rotation
        initialRotationZ?: number; // Initial Z rotation
        forceNormalizeUVs?: boolean; // Force UVs to 0-1 range for max resolution
    };
    defaultLighting?: 'studio' | 'warehouse' | 'city' | 'sunset' | 'park' | 'apartment' | 'lobby' | 'night' | 'dawn' | 'forest';
}

export const availableProducts: Product[] = [
    {
        id: 'diary',
        name: 'Diary',
        category: 'Stationery',
        glbUrl: '/models/Diary.glb',
        thumbnail: '/thumbnails/diary.png',
        description: 'Customizable Leather Diary',
        defaultParts: ['Cover'],
        materialConfig: { 
            invertY: false,
            initialRotationX: 1.5708, // 90 deg vertical rotation
        },
        defaultLighting: 'warehouse',
    },
    {
        id: 'polo-tshirt', // Matches auto-generated ID from modelDiscovery.ts
        name: 'Polo T-Shirt',
        category: 'Apparel',
        glbUrl: '/models/Polo Tshirt.glb',
        thumbnail: '/thumbnails/polo-tshirt.png',
        description: 'Classic polo style with collar',
        defaultParts: ['Body', 'Collar', 'LeftSleeve', 'RightSleeve', 'Cuff'],
        materialConfig: { invertY: false },
        defaultLighting: 'city',
    },
    {
        id: 'pen',
        name: 'Promotional Pen',
        category: 'Stationery',
        glbUrl: '/models/Pen.glb',
        thumbnail: '/thumbnails/pen.png',
        description: 'Branded ballpoint pen',
        defaultParts: ['Body', 'Clip', 'Cap'],
        materialConfig: {
            invertX: true,
            invertY: false,
            textureFlip: false,
            textureFlipX: true,
            initialRotationY: -3.14159, // 180 deg (Rotated 90 more clockwise)
        },
        defaultLighting: 'sunset',
    },
    {
        id: 'glass-award',
        name: 'Glass Award',
        category: 'Awards',
        glbUrl: '/models/Glass Award.glb',
        thumbnail: '/thumbnails/glass-award.png',
        description: 'Premium glass award trophy',
        defaultParts: ['Base', 'Top'],
        materialConfig: {
            useGlassMaterial: true,
            targetScale: 1.0,
            invertY: false,
            initialRotationY: 4.7124, // 270 degrees (Rotated another 180)
        },
        defaultLighting: 'city',
    },
    {
        id: 'tumbler',
        name: 'Tumbler',
        category: 'Drinkware',
        glbUrl: '/models/Tumbler.glb',
        thumbnail: '/thumbnails/tumbler.png',
        description: 'Insulated tumbler cup',
        defaultParts: ['Body', 'Lid'],
        materialConfig: {
            forceCylindricalUVs: true,
            invertY: false,
        },
        defaultLighting: 'warehouse',
    },
    {
        id: 'sports-jersey',
        name: 'Sport Jersey',
        category: 'Apparel',
        glbUrl: '/models/Sports Jersey.glb',
        thumbnail: '/thumbnails/sport-jersey.png',
        description: 'Athletic sports jersey',
        defaultParts: ['Body', 'Sleeves'],
        materialConfig: { 
            invertY: false,
            initialRotationY: 3.14159 // Rotate 180 deg
        },
        defaultLighting: 'city',
    },

    {
        id: 'towel',
        name: 'Towel',
        category: 'Apparel',
        glbUrl: '/models/Towel.glb',
        thumbnail: '/thumbnails/towel.png',
        description: 'Premium branded towel',
        defaultParts: ['Cloth', 'Stich'],
        materialConfig: {
            invertY: false,
            forceNormalizeUVs: true,
            textureFlipX: true,
            invertX: true,
        },
        defaultLighting: 'city',
    },

    {
        id: 'jar',
        name: 'Jar of Jelly Beans',
        category: 'Drinkware', // or 'Stationery' or just 'Others' - trying nearest fit or 'Drinkware' as container
        glbUrl: '/models/Jar.glb',
        thumbnail: '/thumbnails/jar.png', // Assuming thumbnail naming convention
        description: 'Glass jar filled with jelly beans',
        defaultParts: ['Cap', 'Jelly_Beans'],
        materialConfig: {
            useGlassMaterial: true,
            invertY: false,
            initialRotationX: 4.7124, // 270 deg vertical (90 + 180)
            targetScale: 0.7, // Reduced further to 1.0 (from 1.2)
            initialRotationY: 3.14159, // Rotate 180 degrees
        },
        defaultLighting: 'warehouse',
    },
    {
        id: 'square-pen',
        name: 'Performance Pen',
        category: 'Stationery',
        glbUrl: '/models/Square_Pen.glb',
        thumbnail: '/thumbnails/square_pen.png', // Assuming thumbnail
        description: 'Square barrel performance pen',
        defaultParts: ['Body', 'Clip', 'Top', 'Grip', 'Tip'],
        materialConfig: {
            invertY: false, // Standard assumption
            initialRotationY: 0,
            initialRotationZ: 0, 
            initialRotationX: 1.5708, // Rotate +Z (Tip) to -Y (Down)
            // Assuming standard orientation or based on user notes: "Coordinate System: Y-Up. Center Point: (0,0,0) at the base."
        },
        defaultLighting: 'warehouse',
    },
    {
        id: 'dye-sublimated-lanyard-with-metal-crimp',
        name: 'Dye-Sublimated Lanyard',
        category: 'Accessories',
        glbUrl: '/models/Dye-Sublimated Lanyard with Metal Crimp.glb',
        thumbnail: '/thumbnails/dye-sublimated-lanyard.png',
        description: 'Dye-sublimated lanyard with metal crimp',
        defaultParts: ['Strap', 'Clip', 'Crimp'],
        materialConfig: {
            invertY: false,
        },
        defaultLighting: 'city',
    },
    {
        id: 'nylon-lanyard',
        name: 'Nylon Lanyard',
        category: 'Accessories',
        glbUrl: '/models/Nylon Lanyard.glb',
        thumbnail: '/thumbnails/nylon-lanyard.png',
        description: 'Nylon lanyard with safety breakaway',
        defaultParts: ['Strap', 'Clip', 'Safety_Breakaway'],
        materialConfig: {
            invertY: false,
        },
        defaultLighting: 'warehouse',
    },
    {
        id: 'cap',
        name: 'Cap',
        category: 'Apparel',
        glbUrl: '/models/Cap.glb',
        thumbnail: '/thumbnails/cap.png',
        description: 'Customizable baseball cap',
        defaultParts: ['Front', 'Back', 'Bill'],
        materialConfig: {
            invertY: true, // Fix inverted Y-axis dragging
        },
        defaultLighting: 'apartment',
    },
];

export function getProductById(id: string): Product | undefined {
    return availableProducts.find(p => p.id === id);
}

export function getProductsByCategory(category: string): Product[] {
    return availableProducts.filter(p => p.category === category);
}

export const productCategories = [
    'All',
    'Apparel',
    'Stationery',
    'Drinkware',
    'Awards',
    'Accessories',
];
